import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

declare let gtag: Function;

@Injectable({
    providedIn: 'root'
})
export class GoogleAnalyticsService {
    /*
        GA4

        When choosing a name for a new event, follow these rules:
        - Event names are case sensitive. For example, my_event and My_Event are distinct events.
        - Event names can include English and non-English words and letters.
        - Do not use reserved prefixes and event names.
        - Event names must start with a letter. Use only letters, numbers, and underscores. Don't use spaces.

        If adding new events, be sure to include in "studentportal.literacypro.com" container on https://tagmanager.google.com/.

    */
    constructor() {}

    public pageView(pageTitle: string = '') {
        gtag('event', 'page_view', {
            ...this.getCommonProperties(),
            page_title: pageTitle
        });
    }

    /**
     * @param searchedByAddress whether the student filtered by address. The address itself is
     * deliberately not sent — it is typically their home address, and the useful signal is how
     * often location search is used, not where anyone lives.
     * @param searchDistance the radius in miles, 0 when no distance filter is applied.
     */
    public search(
        pageTitle: string = '',
        searchTerm: string = '',
        searchedByAddress: boolean = false,
        searchDistance: number = 0
    ) {
        gtag('event', 'search', {
            ...this.getCommonProperties(),
            page_title: pageTitle,
            search_term: searchTerm,
            searched_by_address: searchedByAddress,
            search_distance: searchDistance
        });
    }

    public click(pageTitle: string = '', eventName: string = '') {
        gtag('event', 'click', {
            ...this.getCommonProperties(),
            page_title: pageTitle,
            event_name: eventName
        });
    }

    public login() {
        gtag('event', 'login', this.getCommonProperties());
    }

    public signUp() {
        gtag('event', 'sign_up', this.getCommonProperties());
    }

    public formError(form: UntypedFormGroup) {
        gtag('event', 'form_error', {
            ...this.getCommonProperties(),
            ...this.getFormValidationSummary(form)
        });
    }

    private getCommonProperties(): any {
        return {
            client: runtimeConfig.clientCode?.toUpperCase(),
            environment: runtimeConfig.environment,
            language: sessionStorage.getItem('selectedLanguage') ?? 'en'
        };
    }

    /**
     * Summarises a failed validation as the names of the failing controls and the kinds of
     * failure — never the values.
     *
     * Two things are deliberately excluded. `form.getRawValue()`, which used to be sent whole,
     * carries everything the student typed: SSN, date of birth, income, housing status. And the
     * Angular error objects themselves are unsafe: `pattern`, `min` and `max` embed the user's
     * input as `actualValue` / `actual`, and all three are wired up from the server-driven schema
     * in core/helpers/validator-registry.ts. Only the error keys are reported.
     *
     * The keys alone are what makes the report actionable anyway — "which field fails, and how
     * often" is a question you can chart; a free-text value is not.
     */
    private getFormValidationSummary(form: UntypedFormGroup): any {
        const invalidFields: string[] = [];
        const errorTypes = new Set<string>();

        Object.keys(form.controls).forEach((key) => {
            const controlErrors = form.get(key)?.errors;
            if (!controlErrors) return;

            invalidFields.push(key);
            Object.keys(controlErrors).forEach((errorKey) =>
                errorTypes.add(errorKey)
            );
        });

        return {
            invalid_fields: invalidFields.join(','),
            invalid_field_count: invalidFields.length,
            error_types: Array.from(errorTypes).join(','),
            form_pristine: form.pristine,
            form_touched: form.touched
        };
    }
}
